import React, { useState, useEffect } from "react";
import { AlertOctagon, AlertTriangle, Bell, CheckCircle2, Info, Clock, Settings, ShieldAlert } from "lucide-react";
import { api } from "../lib/api";
import { Section, KpiCard, InsightsGrid, ChatPanel, Modal, ModuleHeader } from "../components/intel/IntelKit";
import useCustomerData from "../hooks/useCustomerData";
import ConnectTallyBanner from "../components/ConnectTallyBanner";

/** Map calculation-engine alerts → the LiveFeed row shape this module renders. */
function realAlertsFromCustomerData(customerData) {
  if (!customerData) return [];
  const engineAlerts = customerData.alerts || [];
  // Normalise severity: engine uses critical/warning/positive/info,
  // the UI uses critical/medium/info.
  const sevMap = { critical: "critical", warning: "medium", positive: "info", info: "info" };
  return engineAlerts.map((a, i) => ({
    id: a.alert_id || `engine-${i}`,
    sev: sevMap[a.severity] || "info",
    title: a.title,
    action: a.recommended_action || "View details",
    detail: a.description,
    metric_name: a.metric_name,
    metric_value: a.metric_value,
  }));
}

// ---------------------------------------------------------------------------
// SECTION 1 — KPIs
// ---------------------------------------------------------------------------
const KPIS = [
  { id: "open",     label: "Open Anomalies",      sub: "Detected from your live data", status: "red",   icon: AlertOctagon, realKey: "openCount",    format: "number", tooltip: "Live anomalies detected from your Tally data + connected systems." },
  { id: "resolved", label: "Resolved This Month", sub: "Counts as you mark them",      status: "green", icon: CheckCircle2, realKey: null,           format: "number", tooltip: "Counts how many alerts you've marked resolved this month. Builds up as you use the module." },
  { id: "mttr",     label: "Mean Time to Resolve",sub: "After first 10 resolutions",   status: "green", icon: Clock,        realKey: null,           format: "raw",    tooltip: "Average time between alert raised and resolved. Computed after first 10 resolutions." },
  { id: "rules",    label: "Rules Active",        sub: "Detection rules running",      status: "blue",  icon: Settings,     realKey: "rulesActive",  format: "number", tooltip: "Number of detection rules currently active across CEO / CFO / CMO / CTO domains." },
];

function realValueForAlerts(realKey, realAlerts) {
  if (!realKey) return null;
  switch (realKey) {
    case "openCount":   return Array.isArray(realAlerts) && realAlerts.length > 0 ? realAlerts.filter((a) => !a.resolved).length : null;
    case "rulesActive": return Array.isArray(realAlerts) && realAlerts.length > 0 ? 8 : null; // 8 baseline alerts wired in realAlertsFromCustomerData logic
    default: return null;
  }
}

// ---------------------------------------------------------------------------
// SECTION 2 — Live anomaly feed (CRITICAL → MEDIUM → INFO)
// ---------------------------------------------------------------------------
const SEV_META = {
  critical: { tone: "red",   label: "CRITICAL", icon: ShieldAlert, bg: "bg-red-50/70",     border: "border-red-300",     pillBg: "bg-red-100",     pillText: "text-red-700"     },
  medium:   { tone: "amber", label: "MEDIUM",   icon: AlertTriangle, bg: "bg-amber-50/70",  border: "border-amber-300",   pillBg: "bg-amber-100",   pillText: "text-amber-700"   },
  info:     { tone: "blue",  label: "INFO",     icon: Info,       bg: "bg-blue-50/60",    border: "border-blue-300",    pillBg: "bg-blue-100",    pillText: "text-blue-700"    },
};

function LiveFeed({ realAlerts = [] }) {
  // Active customer only: show ONLY engine-generated alerts.
  const [items, setItems] = useState(realAlerts);
  useEffect(() => { setItems(realAlerts); }, [realAlerts]);
  const [modalItem, setModalItem] = useState(null);

  const dismiss = (id) => setItems((arr) => arr.filter((x) => x.id !== id));
  const resolve = (id) => setItems((arr) => arr.map((x) => x.id === id ? { ...x, resolved: true } : x));

  const onAction = (item) => {
    if (item.sev === "critical") {
      setModalItem(item); // CRITICAL opens modal
    } else {
      resolve(item.id);   // MEDIUM/INFO inline state toggle
    }
  };

  const confirmCritical = () => {
    resolve(modalItem.id);
    setModalItem(null);
  };

  return (
    <Section testid="alert-feed" title="Live Anomaly Feed" subtitle="Sorted critical → medium → info · each has dismiss + action CTA">
      <ul className="space-y-2">
        {items.length === 0 && (
          <li data-testid="alert-empty" className="rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-4 text-center text-[12px] text-emerald-700">
            All clear — no open anomalies right now.
          </li>
        )}
        {items.map((a) => {
          const m = SEV_META[a.sev];
          const Icon = m.icon;
          return (
            <li key={a.id} data-testid={`alert-row-${a.id}`}
              className={`flex flex-col gap-2 rounded-lg ${m.bg} border-l-4 ${m.border} px-3 py-2.5 md:flex-row md:items-center md:justify-between`}>
              <div className="flex items-start gap-2">
                <span className={`mt-0.5 inline-flex items-center gap-1 rounded-full ${m.pillBg} px-2 py-0.5 text-[10.5px] font-bold ${m.pillText}`}>
                  <Icon size={11} /> {m.label}
                </span>
                <div>
                  <div className={`text-[12.5px] font-semibold text-[#0F1B2D] ${a.resolved ? "line-through opacity-60" : ""}`}>{a.title}</div>
                  <div className="mt-0.5 text-[11.5px] text-slate-500">Action: {a.action}</div>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2 md:self-center">
                {a.resolved ? (
                  <span data-testid={`alert-resolved-${a.id}`} className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-1 text-[11px] font-semibold text-emerald-700"><CheckCircle2 size={12} /> Resolved</span>
                ) : (
                  <>
                    <button data-testid={`alert-action-${a.id}`} onClick={() => onAction(a)}
                      className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-[11.5px] font-semibold ${a.sev === "critical" ? "bg-red-600 text-white hover:bg-red-700" : a.sev === "medium" ? "bg-[#1E3A5F] text-white hover:bg-[#0F1B2D]" : "border border-slate-300 bg-white text-[#1E3A5F] hover:bg-slate-50"}`}>
                      {a.action}
                    </button>
                    <button data-testid={`alert-dismiss-${a.id}`} onClick={() => dismiss(a.id)}
                      className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[11.5px] text-slate-500 hover:text-[#0F1B2D]">
                      Dismiss
                    </button>
                  </>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      <Modal open={!!modalItem} onClose={() => setModalItem(null)} title={modalItem ? `Critical · ${modalItem.action}` : "Critical"} testid="alert-modal">
        {modalItem && (
          <div className="space-y-3">
            <div className="rounded-lg bg-slate-50 px-3 py-2 text-[12.5px]">
              <div className="font-semibold text-[#0F1B2D]">{modalItem.title}</div>
            </div>
            <div className="rounded-lg border-l-4 border-red-500 bg-red-50/60 p-3 text-[12px] text-slate-700">
              {modalItem.detail}
            </div>
            <div className="flex items-center gap-2">
              <button data-testid="alert-modal-confirm" onClick={confirmCritical}
                className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-md bg-emerald-600 px-3 py-2 text-[12.5px] font-semibold text-white hover:bg-emerald-700">
                <CheckCircle2 size={12} /> Confirm — {modalItem.action}
              </button>
              <button data-testid="alert-modal-cancel" onClick={() => setModalItem(null)}
                className="inline-flex items-center justify-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-2 text-[12.5px] font-semibold text-[#1E3A5F] hover:bg-slate-50">
                Cancel
              </button>
            </div>
          </div>
        )}
      </Modal>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// SECTION 3 — Rules by domain + delivery channels (honest empty states until
// per-customer rule and delivery telemetry is wired)
// ---------------------------------------------------------------------------
function RulesAndChannels() {
  return (
    <div className="grid grid-cols-1 gap-4 tablet:grid-cols-2 desktop:grid-cols-2">
      <Section testid="alert-rules" title="Rules by domain" subtitle="Per-domain rule counts appear once detection rules are wired">
        <div data-testid="alert-rules-empty" className="flex items-start gap-2 rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-6 text-[12px] text-slate-500">
          <span className="text-slate-400 text-[18px] leading-none">—</span>
          <span>Domain-level rule counts (Finance, GST, Security, CRM, Operations) will populate once detection rules ship from the engine.</span>
        </div>
      </Section>

      <Section testid="alert-channels" title="Delivery channels" subtitle="Where alerts land">
        <div data-testid="alert-channels-empty" className="flex items-start gap-2 rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-6 text-[12px] text-slate-500">
          <span className="text-slate-400 text-[18px] leading-none">—</span>
          <span>WhatsApp / email / dashboard delivery shares populate after at least 30 days of alert dispatches.</span>
        </div>
      </Section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Module shell
// ---------------------------------------------------------------------------
export default function SmartAlerts() {
  const { dataAvailable, customerData, isLoading } = useCustomerData();
  const realAlerts = dataAvailable ? realAlertsFromCustomerData(customerData) : [];
  return (
    <div data-testid="alert-module" className="space-y-5">
      <ConnectTallyBanner dataAvailable={dataAvailable} isLoading={isLoading} />
      <div className="px-4 tablet:px-5 desktop:px-6 py-4 desktop:py-5 space-y-5">
      <ModuleHeader
        overline="Anomaly Detection & Smart Alerts"
        title="Catch what humans miss · 24/7"
        right={<><Bell size={13} className="text-emerald-600" />{(dataAvailable ? `${realAlerts.length} live` : "Connect Tally for live alerts")}</>}
      />

      <section data-testid="alert-kpis" className="grid grid-cols-2 gap-3 desktop:grid-cols-4">
        {KPIS.map((k) => (
          <KpiCard
            key={k.id}
            kpi={k}
            testidPrefix="alert-kpi"
            isLoading={isLoading}
            dataAvailable={dataAvailable}
            realValue={realValueForAlerts(k.realKey, realAlerts)}
          />
        ))}
      </section>

      <LiveFeed realAlerts={realAlerts} />

      <RulesAndChannels />

      <InsightsGrid
        subtitle="What your Smart Alerts advisor would tell you today"
        fetchFn={(force) => api.aiInsightsGenerate("alerts", api.alertsInsights, force)}
        testidPrefix="alert"
        dataAvailable={dataAvailable}
      />

      <ChatPanel
        title="Ask your Smart Alerts advisor"
        subtitle="Risks, anomalies, prioritisation · Hindi or English"
        placeholder="Ask which alert to act on first, or how to add a rule…"
        sendFn={api.alertsChat}
        testidPrefix="alert"
        emptyHint="Ask about your alerts. Try: 'Which alert should I act on first?' or 'How do I add a new threshold rule?'"
      />
      </div>
    </div>
  );
}
